import { KaboodleToaster } from "components/common";
import {
  InvoiceBatchSelectColumn,
  Section111SelectColumn,
} from "components/Table/ConditionalSelectColumn";
import { Tooltip } from "components/Tooltip/Tooltip";
import React, { useState } from "react";
import {
  useGenerateFinanceReportAsyncMutation,
  useGenerateFinanceReportMutation,
  useGetReportDataMutation,
} from "services/finance-api";

import type { FinanceFilters } from "../utils/reportUtils";
import {
  buildReportOptions,
  convertSortToOrderBy,
  generateReportFilename,
  isServerSideSortingReport,
} from "../utils/reportUtils";

import type { ServerSortColumn } from "components/Table/Table";

import type { Column } from "react-data-grid";
const FINANCE_PAGE_SIZE = 50;
const FINANCE_OFFSET = (page: number) => (page - 1) * FINANCE_PAGE_SIZE;

type UseReportOperationsProps = {
  urlStateManager: {
    updateUrl: (filters: FinanceFilters, page?: number) => void;
  };
  updateUrlWithCurrentFilters: () => void;
};

/*
 * hook for report operations, ie. generating reports, viewing report data, etc.
 */
export const useReportOperations = ({
  urlStateManager,
  updateUrlWithCurrentFilters,
}: UseReportOperationsProps) => {
  const [reportData, setReportData] = useState<Record<string, unknown>[]>([]);
  const [reportColumns, setReportColumns] = useState<
    Column<Record<string, unknown>>[]
  >([]);
  const [showReportTable, setShowReportTable] = useState(false);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(
    new Set(),
  );
  const [reportMeta, setReportMeta] = useState<Record<string, unknown> | null>(
    null,
  );
  const [sortColumns, setSortColumns] = useState<readonly ServerSortColumn[]>(
    [],
  );

  // Sync mutation is retained for the LEDES export — that endpoint
  // returns a small text file inline and does not need the async
  // queue.
  const [generateFinanceReport, { isLoading: isGeneratingSync }] =
    useGenerateFinanceReportMutation();
  const [generateFinanceReportAsync, { isLoading: isGeneratingAsync }] =
    useGenerateFinanceReportAsyncMutation();
  const [getReportData, { isLoading: isLoadingReportData }] =
    useGetReportDataMutation();

  const onGenerateReport = async (data: FinanceFilters) => {
    // update URL with current filters
    updateUrlWithCurrentFilters();

    // All finance reports now go through the async-export + email path
    // on the backend (`generate-report-async/`). The sync
    // `/generate-report/` endpoint pressures the web tier — building
    // and streaming the XLSX inline holds a gunicorn worker open until
    // the file is done, which times out for large filters. The FE
    // posts the request, gets a job id back, and the user receives the
    // XLSX as an email attachment when it's ready.
    try {
      const reportOptions = buildReportOptions(data);
      await generateFinanceReportAsync({ data: reportOptions }).unwrap();
      KaboodleToaster.success(
        "Your report is being generated. We'll email it to you when it's ready — you can also track progress in the activity drawer.",
      );
    } catch (error: unknown) {
      console.error("Error generating report:", error);
      const errorMessage =
        (
          error as {
            data?: {
              errors?: {
                general?: string[];
                options?: string[];
                report_type?: string[];
              };
            };
          }
        )?.data?.errors?.general?.[0] ||
        (
          error as {
            data?: {
              errors?: {
                general?: string[];
                options?: string[];
                report_type?: string[];
              };
            };
          }
        )?.data?.errors?.options?.[0] ||
        (
          error as {
            data?: {
              errors?: {
                general?: string[];
                options?: string[];
                report_type?: string[];
              };
            };
          }
        )?.data?.errors?.report_type?.[0] ||
        "Failed to queue report";
      KaboodleToaster.error(errorMessage);
    }
  };

  const onGenerateLedesReport = async (data: FinanceFilters) => {
    // update URL with current filters
    updateUrlWithCurrentFilters();

    try {
      const reportOptions = buildReportOptions(data, { exportAsLedes: true });
      const customFilename = generateReportFilename(data, {
        exportAsLedes: true,
      });

      await generateFinanceReport({
        data: reportOptions,
        customFilename,
      }).unwrap();

      KaboodleToaster.success("LEDES export generated successfully");
    } catch (error: unknown) {
      console.error("Error generating LEDES export:", error);
      const errorMessage =
        (
          error as {
            data?: { errors?: { general?: string[]; options?: string[] } };
          }
        )?.data?.errors?.general?.[0] ||
        (
          error as {
            data?: { errors?: { general?: string[]; options?: string[] } };
          }
        )?.data?.errors?.options?.[0] ||
        "Failed to generate LEDES export";
      KaboodleToaster.error(errorMessage);
    }
  };

  const onViewReportData = async (
    data: FinanceFilters,
    pageToLoad = 1,
    overrideSortColumns?: readonly ServerSortColumn[],
  ) => {
    // update URL with current filters
    updateUrlWithCurrentFilters();

    try {
      // only send sorting for server-side sorting reports
      const supportsServerSorting = isServerSideSortingReport(data.report_type);

      let reportOptions;
      if (supportsServerSorting) {
        // use override sort columns if provided, otherwise use state
        const activeSortColumns = overrideSortColumns ?? sortColumns;
        const orderBy = convertSortToOrderBy(activeSortColumns);
        const dataWithSorting = {
          ...data,
          order_by: orderBy,
        };
        reportOptions = buildReportOptions(dataWithSorting);
      } else {
        // for client-side sorting reports, don't send order_by
        reportOptions = buildReportOptions(data);
      }

      const response = await getReportData({
        data: reportOptions,
        limit: FINANCE_PAGE_SIZE,
        offset: FINANCE_OFFSET(pageToLoad),
      }).unwrap();

      const tableColumns = response.columns
        .filter((col) => !col.hidden) // remove ID column
        .filter((col) => {
          // hide Invoice No column for Work in Progress reports
          if (
            col.key === "Invoice No" &&
            data.report_type?.value === "WORK_IN_PROGRESS"
          ) {
            return false;
          }
          return true;
        })
        .map((col) => ({
          key: col.key,
          name: col.name,
          // ensure sortable columns have minimum width for sort indicator (text + ~25px for arrow)
          minWidth: col.key === "Service URL" ? undefined : 120,
          // constrain description column width
          ...(col.key.toLowerCase().includes("description") && {
            width: 300,
          }),
          // disable sorting on Service URL and Claim ID columns
          ...((col.key === "Service URL" || col.key === "Claim ID") && {
            sortable: false,
          }),
          renderCell:
            col.type === "currency"
              ? ({ row }: { row: Record<string, unknown> }) =>
                  String(row[col.key] || "")
              : col.type === "date"
                ? ({ row }: { row: Record<string, unknown> }) =>
                    String(row[col.key] || "")
                : col.type === "claim_link"
                  ? ({ row }: { row: Record<string, unknown> }) => {
                      const claimId = String(row[col.key] || "");
                      const claimUrl = String(row["Claim URL"] || "");
                      if (!claimId || claimId === "null") {
                        return "-";
                      }
                      if (!claimUrl || claimUrl === "null") {
                        return claimId;
                      }
                      const fullUrl = claimUrl.startsWith("http")
                        ? claimUrl
                        : `https://${claimUrl}`;
                      return (
                        <a
                          href={fullUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="service-url-link"
                        >
                          {claimId}
                        </a>
                      );
                    }
                  : col.type === "url"
                    ? ({ row }: { row: Record<string, unknown> }) => {
                        const url = String(row[col.key] || "");
                        if (!url || url === "null") {
                          return "-";
                        }

                        const fullUrl = url.startsWith("http")
                          ? url
                          : `https://${url}`;

                        return (
                          <a
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="service-url-link"
                          >
                            View Service
                          </a>
                        );
                      }
                    : // force description text to stay within cell bounds
                      col.key.toLowerCase().includes("description")
                      ? ({ row }: { row: Record<string, unknown> }) => {
                          const text = String(row[col.key] || "");
                          return (
                            <Tooltip
                              content={text}
                              place="top"
                              anchorStyle="block-full"
                            >
                              <div
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  width: "100%",
                                  maxWidth: "100%",
                                }}
                              >
                                {text}
                              </div>
                            </Tooltip>
                          );
                        }
                      : undefined,
        }));

      const finalColumns =
        data.report_type?.value === "INVOICE_LIST"
          ? [InvoiceBatchSelectColumn, ...tableColumns]
          : data.report_type?.value === "SECTION_111_INVOICING"
            ? [Section111SelectColumn, ...tableColumns]
            : tableColumns;

      setReportColumns(finalColumns);
      setReportData(response.data);
      setTotalRecords(response.count);
      setReportMeta(response.meta || null);
      setPage(pageToLoad);
      setShowReportTable(true);

      // for client-side sorting reports, clear server sort state
      if (!supportsServerSorting) {
        setSortColumns([]);
      }
      // for server-side sorting reports, sortColumns state is already maintained locally

      // clear selected rows when loading new data
      if (pageToLoad === 1) {
        setSelectedRows(new Set());
      }

      // removed toast for cleaner auto-run experience (Linear-style)
    } catch (error: unknown) {
      console.error("Error loading report data:", error);
      const errorMessage =
        (
          error as {
            data?: { errors?: { general?: string[]; options?: string[] } };
          }
        )?.data?.errors?.general?.[0] ||
        (
          error as {
            data?: { errors?: { general?: string[]; options?: string[] } };
          }
        )?.data?.errors?.options?.[0] ||
        "Failed to load report data";
      KaboodleToaster.error(errorMessage);
    }
  };

  const handlePageChange = async (
    newPage: number,
    currentFormData: FinanceFilters,
  ) => {
    // when changing pages, maintain current sorting
    await onViewReportData(currentFormData, newPage, sortColumns);

    // update URL with new page
    if (urlStateManager) {
      urlStateManager.updateUrl(currentFormData, newPage);
    }
  };

  const handleSortChange = async (
    newSortColumns: readonly ServerSortColumn[],
    currentFormData: FinanceFilters,
  ) => {
    setSortColumns(newSortColumns);
    // reload data with new sorting - pass the new sort columns directly
    await onViewReportData(currentFormData, page, newSortColumns);
  };

  const resetReportState = () => {
    setShowReportTable(false);
    setReportData([]);
    setReportColumns([]);
    setPage(1);
    setTotalRecords(0);
    setSelectedRows(new Set());
    setReportMeta(null);
    setSortColumns([]);
  };

  return {
    reportState: {
      reportData,
      reportColumns,
      showReportTable,
      page,
      totalRecords,
      selectedRows,
      setSelectedRows,
      reportMeta,
      sortColumns,
    },
    operations: {
      onGenerateReport,
      onGenerateLedesReport,
      onViewReportData,
      handlePageChange,
      handleSortChange,
      resetReportState,
    },
    loading: {
      // ``isGenerating`` keeps the existing disabled-button UX while
      // the queue-the-job request is in flight. The LEDES export still
      // uses the sync mutation, so we OR both so either one disables
      // the export buttons.
      isGenerating: isGeneratingAsync || isGeneratingSync,
      isLoadingReportData,
    },
    constants: {
      FINANCE_PAGE_SIZE,
    },
  };
};
